import { logger } from '@adornis/base/logging.js';
import { baseqlMetaData } from '@adornis/baseql/metadata/metaDataStore.js';
import { registerMutation, registerQuery } from '@adornis/baseql/metadata/register.js';
import { getCollection, getRawCollection } from '@adornis/baseql/server/collections.js';
import { context } from '@adornis/baseql/server/context.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { ChancenPersonalConfig } from '../entities/ChancenPersonalConfig.js';

const resolveGetPersonalConfigForKeyAndCategory = <T extends ChancenPersonalConfig>(
  className: string,
  category: string,
  key: string,
) => {
  return async (gqlFields: BaseQLSelectionSet<T>) => {
    if (context.serverContext || !context.userID) return null;

    if (!category) throw new Error('Tried to get config but no category was found!');
    if (!key) throw new Error('Tried to get config but no key was found!');

    const selector = {
      category,
      key,
      userID: context.userID,
    };

    const col = await getCollection<ChancenPersonalConfig>(className);

    while ((await col.countDocuments(selector)) > 1) {
      logger.warn('Multiple configs found for ' + className + ' ' + category + ' ' + key + ' ' + context.userID);
      await col.deleteOne(selector);
    }

    const result = await col.findOne(selector);
    const isEditOrCreate = category.startsWith('edit-') || category.startsWith('create-');

    if (!result || isEditOrCreate) {
      const ClassOfConfig = baseqlMetaData.entities.resolveNode(className).type() as typeof ChancenPersonalConfig;
      if (!ClassOfConfig) {
        throw new Error(
          'Tried to get default for class ' + className + ' but the class was not found in the MetaDataTree!',
        );
      }

      const initialisedDefault = ClassOfConfig.getDefault(category, key);
      if (!initialisedDefault) throw new Error("Can't generate Config: " + key);

      initialisedDefault.userID = context.serverContext ? '' : context.userID;
      if (!isEditOrCreate) {
        initialisedDefault._id = await initialisedDefault.create();
      }

      return initialisedDefault as T;
    }

    return result as T;
  };
};

const getPersonalConfigForKeyAndCategoryOP = registerQuery({
  type: () => ChancenPersonalConfig,
  operationName: 'getPersonalConfigForKeyAndCategoryOp',
  params: [
    { type: () => String, name: 'className' },
    { type: () => String, name: 'category' },
    { type: () => String, name: 'key' },
  ],
  resolve: resolveGetPersonalConfigForKeyAndCategory,
});

export const getPersonalConfigForKeyAndCategory = <T extends typeof ChancenPersonalConfig>(
  entity: T,
  category: string,
  key: string,
) => getPersonalConfigForKeyAndCategoryOP<T['prototype']>(entity._class, category, key);

const resetPersonalConfigForKeyAndCategoryOP = registerMutation({
  type: () => Boolean,
  operationName: 'resetPersonalConfigForKeyAndCategoryOp',
  params: [
    { type: () => String, name: 'className' },
    { type: () => String, name: 'category' },
    { type: () => String, name: 'key' },
  ],
  resolve: (className: string, category: string, key: string) => {
    return async () => {
      if (context.serverContext || !context.userID) return false;

      if (!category) throw new Error('Tried to reset config but no category was found!');
      if (!key) throw new Error('Tried to reset config but no key was found!');

      const col = await getRawCollection<ChancenPersonalConfig>(ChancenPersonalConfig._collectionName);
      await col.deleteMany({
        category,
        key,
        userID: context.userID,
      });

      return true;
    };
  },
});

export const resetPersonalConfigForKeyAndCategory = <T extends typeof ChancenPersonalConfig>(
  entity: T,
  category: string,
  key: string,
) => resetPersonalConfigForKeyAndCategoryOP(entity._class, category, key);

export namespace ChancenPersonalConfigAPI {
  export const getByKeyAndCategory = getPersonalConfigForKeyAndCategory;
  export const resetByKeyAndCategory = resetPersonalConfigForKeyAndCategory;
}
